import { Injectable } from '@angular/core';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../../core/reducers';
import {
  PharmacyGroupActionTypes,
  PharmacyGroupsPageRequested,
  PharmacyGroupsPageLoaded,
  PharmacyGroupCreated,
  PharmacyGroupDeleted,
  PharmacyGroupUpdated,
  PharmacyGroupOnServerCreated,
  PharmacyGroupsActionToggleLoading,
  PharmacyGroupsPageToggleLoading,
  ThrowPharmacyGroupError,
  FirePharmacyGroupSuccess,
  PharmacyGroupOnServerUpdated,
  PharmacyGroupsPageCancelled,
  PharmacyGroupOnServerDeleted,
} from '../_actions/pharmacy-group.actions';
import { Router } from '@angular/router';
import { PharmacyGroupService } from '../_services';
import { AuthService } from '../../auth/_services';

@Injectable()
export class PharmacyGroupEffects {
  showPageLoadingDistpatcher = new PharmacyGroupsPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new PharmacyGroupsPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new PharmacyGroupsActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new PharmacyGroupsActionToggleLoading({ isLoading: false });

  @Effect()
  loadPharmacyGroupsPage$ = this.actions$.pipe(
    ofType<PharmacyGroupsPageRequested>(PharmacyGroupActionTypes.PharmacyGroupsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.pharmacyGroup.findPharmacyGroups(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin([requestToServer, lastQuery]).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0] && response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new PharmacyGroupsPageCancelled();
            } else {
              this.store.dispatch(new ThrowPharmacyGroupError({ error: response[0].error.error }));
              return new PharmacyGroupsPageCancelled();
            }
          } else {
            const result = response[0];
            return new PharmacyGroupsPageLoaded({
              pharmacyGroups: result.data,
              totalCount: result.count,
              page: response[1],
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deletePharmacyGroup$ = this.actions$.pipe(
    ofType<PharmacyGroupOnServerDeleted>(PharmacyGroupActionTypes.PharmacyGroupOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.pharmacyGroup.deletePharmacyGroup(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPharmacyGroupError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FirePharmacyGroupSuccess({
                action_type: PharmacyGroupActionTypes.PharmacyGroupDeleted,
              }),
            );
            return new PharmacyGroupDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePharmacyGroup$ = this.actions$.pipe(
    ofType<PharmacyGroupOnServerUpdated>(PharmacyGroupActionTypes.PharmacyGroupOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.pharmacyGroup.updatePharmacyGroup(payload.pharmacyGroup).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPharmacyGroupError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FirePharmacyGroupSuccess({
                action_type: PharmacyGroupActionTypes.PharmacyGroupUpdated,
              }),
            );
            return new PharmacyGroupUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createPharmacyGroup$ = this.actions$.pipe(
    ofType<PharmacyGroupOnServerCreated>(PharmacyGroupActionTypes.PharmacyGroupOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.pharmacyGroup.createPharmacyGroup(payload.pharmacyGroup).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPharmacyGroupError({ error: res.error });
          } else {
            this.store.dispatch(
              new FirePharmacyGroupSuccess({
                action_type: PharmacyGroupActionTypes.PharmacyGroupCreated,
              }),
            );
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PharmacyGroupCreated({ pharmacyGroup: res });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private pharmacyGroup: PharmacyGroupService,
    private store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
  ) {}
}
